import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
} from '@mui/material';
import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import Badge from '@/components/Badge';
import { ConsolidatedSkill } from '@/services/workersV2API';
import { axiosRubyClient } from '@/util/axios/axiosClient';

type Props = {
  skill: ConsolidatedSkill;
  workerId: number;
  onUpdate: () => void;
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const formatTime = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes}m ${secs}s`;
};

const QuizAssessmentTab = ({ skill, workerId, onUpdate }: Props) => {
  const [showRetryDialog, setShowRetryDialog] = useState(false);
  const [retrySettings, setRetrySettings] = useState({
    attemptCount: skill.skill_request?.assessment_attempt_count ?? 0,
    retryEligibleAt: skill.skill_request?.assessment_retry_eligible_at
      ? new Date(skill.skill_request.assessment_retry_eligible_at)
          .toISOString()
          .slice(0, 16)
      : '',
  });
  const [loadingPass, setLoadingPass] = useState(false);
  const [loadingFail, setLoadingFail] = useState(false);
  const [loadingAdminPass, setLoadingAdminPass] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);

  const assessment = skill.assessment_answer;
  const skillRequest = skill.skill_request;

  const handleMarkAssessment = async (passed: boolean) => {
    if (!skillRequest) return;

    const setLoading = passed ? setLoadingPass : setLoadingFail;
    setLoading(true);

    try {
      await axiosRubyClient.patch(
        `/api/admin/skill_requests/${skillRequest.id}/assessment`,
        { passed },
      );
      onUpdate();
    } catch (error) {
      console.error('Error updating assessment:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdminPass = async () => {
    setLoadingAdminPass(true);

    try {
      await axiosRubyClient.post(
        `/api/admin/workers/${workerId}/skills/${skill.skill_id}/admin_pass`,
        { work_experience_completed: true },
      );
      onUpdate();
    } catch (error) {
      console.error('Error with admin pass:', error);
    } finally {
      setLoadingAdminPass(false);
    }
  };

  const handleSaveRetrySettings = async () => {
    if (!skillRequest) return;

    setLoadingSave(true);
    setShowRetryDialog(false);

    try {
      await axiosRubyClient.patch(
        `/api/admin/skill_requests/${skillRequest.id}/retry_settings`,
        {
          assessment_attempt_count: retrySettings.attemptCount,
          assessment_retry_eligible_at: retrySettings.retryEligibleAt
            ? new Date(retrySettings.retryEligibleAt).toISOString()
            : null,
        },
      );
      onUpdate();
    } catch (error) {
      console.error('Error updating retry settings:', error);
    } finally {
      setLoadingSave(false);
    }
  };

  const cellStyle = {
    borderBottom: '1px solid #E5E7EB',
    padding: '10px 16px',
  };

  const labelCellStyle = {
    ...cellStyle,
    width: '200px',
    color: '#6B7280',
    fontWeight: 500,
  };

  return (
    <Stack vertical gap={24} style={{ width: '100%' }}>
      {/* Assessment Results */}
      <Card>
        <Card.Section>
          <Body weight="semibold" style={{ marginBottom: '16px' }}>
            Quiz Assessment
          </Body>

          {!assessment ? (
            <Small color="lighter">No quiz assessment found</Small>
          ) : (
            <Paper variant="outlined" style={{ borderRadius: '8px' }}>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={labelCellStyle}>ID</TableCell>
                    <TableCell sx={cellStyle}>{assessment.id}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={labelCellStyle}>Total Questions</TableCell>
                    <TableCell sx={cellStyle}>
                      {assessment.total_questions}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={labelCellStyle}>Correct Answers</TableCell>
                    <TableCell sx={cellStyle}>
                      {assessment.correct_answers_count}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={labelCellStyle}>Percentage</TableCell>
                    <TableCell sx={cellStyle}>
                      {assessment.percentage.toFixed(1)}%
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={labelCellStyle}>Passing</TableCell>
                    <TableCell sx={cellStyle}>
                      <Badge
                        label={assessment.is_passing ? 'Yes' : 'No'}
                        status={assessment.is_passing ? 'theme' : 'danger'}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={labelCellStyle}>Time Taken</TableCell>
                    <TableCell sx={cellStyle}>
                      {formatTime(assessment.total_time_seconds)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={labelCellStyle}>Answered At</TableCell>
                    <TableCell sx={cellStyle}>
                      {formatDate(assessment.answered_at)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ ...labelCellStyle, borderBottom: 'none' }}>
                      Created At
                    </TableCell>
                    <TableCell sx={{ ...cellStyle, borderBottom: 'none' }}>
                      {formatDate(assessment.created_at)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>
          )}
        </Card.Section>
      </Card>

      {/* Retry Settings */}
      {skillRequest && (
        <Card>
          <Card.Section>
            <Stack
              style={{
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
              }}
            >
              <Body weight="semibold">Retry Settings</Body>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setRetrySettings({
                    attemptCount: skillRequest.assessment_attempt_count,
                    retryEligibleAt: skillRequest.assessment_retry_eligible_at
                      ? new Date(skillRequest.assessment_retry_eligible_at)
                          .toISOString()
                          .slice(0, 16)
                      : '',
                  });
                  setShowRetryDialog(true);
                }}
              >
                Edit Retry Settings
              </Button>
            </Stack>

            <Paper variant="outlined" style={{ borderRadius: '8px' }}>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={labelCellStyle}>Attempt Count</TableCell>
                    <TableCell sx={cellStyle}>
                      {skillRequest.assessment_attempt_count} / 3
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={labelCellStyle}>
                      Can Retry Assessment
                    </TableCell>
                    <TableCell sx={cellStyle}>
                      <Badge
                        label={skillRequest.can_retry_assessment ? 'Yes' : 'No'}
                        status={
                          skillRequest.can_retry_assessment ? 'theme' : 'danger'
                        }
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={labelCellStyle}>Retry Eligible At</TableCell>
                    <TableCell sx={cellStyle}>
                      {skillRequest.assessment_retry_eligible_at
                        ? formatDate(skillRequest.assessment_retry_eligible_at)
                        : '-'}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ ...labelCellStyle, borderBottom: 'none' }}>
                      Permanently Ineligible
                    </TableCell>
                    <TableCell sx={{ ...cellStyle, borderBottom: 'none' }}>
                      <Badge
                        label={
                          skillRequest.assessment_permanently_ineligible
                            ? 'Yes'
                            : 'No'
                        }
                        status={
                          skillRequest.assessment_permanently_ineligible
                            ? 'danger'
                            : 'theme'
                        }
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>
          </Card.Section>
        </Card>
      )}

      {/* Admin Actions */}
      {skillRequest && (
        <Card>
          <Card.Section>
            <Stack
              style={{
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
              }}
            >
              <Body weight="semibold">Admin Actions</Body>
              <Badge
                label={
                  skillRequest.assessment_passed
                    ? 'Passed'
                    : assessment
                      ? 'Failed'
                      : 'Not Taken'
                }
                status={
                  skillRequest.assessment_passed
                    ? 'theme'
                    : assessment
                      ? 'danger'
                      : 'warning'
                }
              />
            </Stack>

            <Stack gap={12} style={{ flexWrap: 'wrap' }}>
              {!skillRequest.assessment_passed && (
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  disabled={loadingPass}
                  onClick={() => handleMarkAssessment(true)}
                  startIcon={
                    loadingPass ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                >
                  {loadingPass ? 'Processing...' : 'Mark Assessment Passed'}
                </Button>
              )}

              {skillRequest.assessment_passed && (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  disabled={loadingFail}
                  onClick={() => handleMarkAssessment(false)}
                  startIcon={
                    loadingFail ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                >
                  {loadingFail ? 'Processing...' : 'Mark Assessment Failed'}
                </Button>
              )}

              {!skill.worker_skill && (
                <Button
                  variant="outlined"
                  size="small"
                  disabled={loadingAdminPass}
                  onClick={handleAdminPass}
                  startIcon={
                    loadingAdminPass ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                >
                  {loadingAdminPass
                    ? 'Processing...'
                    : 'Admin Pass (Skip Assessment)'}
                </Button>
              )}
            </Stack>
          </Card.Section>
        </Card>
      )}

      {/* Edit Retry Settings Dialog */}
      <Dialog
        open={showRetryDialog}
        onClose={() => setShowRetryDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Retry Settings</DialogTitle>
        <DialogContent>
          <Stack vertical gap={16} style={{ paddingTop: '8px' }}>
            <TextField
              label="Assessment Attempt Count"
              type="number"
              fullWidth
              value={retrySettings.attemptCount}
              onChange={(e) =>
                setRetrySettings({
                  ...retrySettings,
                  attemptCount: Math.min(
                    3,
                    Math.max(0, parseInt(e.target.value) || 0),
                  ),
                })
              }
              inputProps={{ min: 0, max: 3 }}
              helperText="Number of assessment attempts (0-3). Worker becomes permanently ineligible at 3."
            />
            <TextField
              label="Retry Eligible At"
              type="datetime-local"
              fullWidth
              value={retrySettings.retryEligibleAt}
              onChange={(e) =>
                setRetrySettings({
                  ...retrySettings,
                  retryEligibleAt: e.target.value,
                })
              }
              InputLabelProps={{ shrink: true }}
              helperText="When the worker can retry the assessment. Leave empty to allow immediate retry."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRetryDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSaveRetrySettings}
            variant="contained"
            color="primary"
            disabled={loadingSave}
          >
            {loadingSave ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default QuizAssessmentTab;
